import React, { useEffect, useMemo, useState } from 'react';
import ServeContainer from './container';
import { useGetStaffsListQuery } from 'pages/manageAccounts/services';
import { useChangeOrderItemStatusMutation, useGetOrdersPanelQuery } from './services';
import { groupByStatus } from 'utils/function';
import { validTransitions } from 'utils/constant';
import { useGetStationsQuery } from 'pages/stations/services';
import ConformChefModal from 'components/Modal/ConformChef/ConformChefModal';
import { useSocket } from 'context/SocketContext';

export default function Serve() {
  const [selectedChef, setSelectedChef] = useState('all');
  const { data: rawOrders, isLoading, isFetching, refetch } = useGetOrdersPanelQuery({ chef_id: selectedChef }, { refetchOnMountOrArgChange: true })
  const [flattened, setFlattened] = useState([]);
  const [columns, setColumns] = useState({ ready_to_prepare: [], preparing: [], ready_to_serve: [], served: [] });
  const [isFetchLoading, setIsFetchLoading] = useState(false);

  const { data: chefsList } = useGetStaffsListQuery({ page: 'all', limit: 'all', role: 'chef' });
  const { data: stationsData } = useGetStationsQuery({ page: 'all', limit: 'all' });

  const [changeItemStatusApi] = useChangeOrderItemStatusMutation();
  const socket = useSocket();

  useEffect(() => {
    if (!socket) return;

    const handleUpdate = (data) => {
      console.log("Socket update received in Serve panel:", data);
      refetch();
    };

    socket.on('order_status_update', handleUpdate);
    socket.on('order_item_status_update', handleUpdate);
    socket.on('order_items_update', handleUpdate);
    socket.on('table_status_update', handleUpdate);

    return () => {
      socket.off('order_status_update', handleUpdate);
      socket.off('order_item_status_update', handleUpdate);
      socket.off('order_items_update', handleUpdate);
      socket.off('table_status_update', handleUpdate);
    };
  }, [socket, refetch]);

  // modal state for chef assignment when moving from ready_to_prepare -> preparing
  const [chefModalOpen, setChefModalOpen] = useState(false);
  const [modalOrderItem, setModalOrderItem] = useState(null); // { id, from, to, item }
  const [assignLoading, setAssignLoading] = useState(false);

  // whenever rawOrders changes, build our card list + columns
  // useEffect(() => {
  //   if (!rawOrders?.data?.orders?.length) return;
  //   console.log(rawOrders,"rawOrdersss USEEFFECT RUNNINGG")
  //   // 1) build flat list
  //   const cards = [];
  //   rawOrders?.data?.orders?.forEach(order => {
  //     const tableLabel = order.tableInfo?.table_no || order.order_type.replace('_','‑').toUpperCase();
  //     order.orderItems.forEach(item => {
  //       cards.push({
  //         id:        String(item.id),
  //         order_no:  order.order_id,
  //         statusTimestamp: item[`${item.item_status}_at`],  // ← pull through
  //         item_name: item.menu_item_name,
  //         quantity:  item.quantity,
  //         add_ons:   item.orderItemAddons.map(a => a.addon_name),  
  //         notes:     item.notes || '',   // if you have a notes field in your API
  //         table:     tableLabel,
  //         status:    item.item_status,
  //       });
  //     });
  //   });
  //   setFlattened(cards);
  //   setColumns(groupByStatus(cards));
  // }, [rawOrders]);

  useEffect(() => {
    // If there's no data at all, clear everything
    const orders = rawOrders?.data?.orders ?? [];
    // 1) build flat list
    const cards = [];
    for (const order of orders) {
      const tableLabel = order.tableInfo?.table_no
        || order.order_type.replace('_', '‑').toUpperCase();
      for (const item of order.orderItems) {
        cards.push({
          id: String(item.id),
          order_no: order.order_id,
          statusTimestamp: item[`${item.item_status}_at`],
          item_name: item.menu_item_name,
          quantity: item.quantity,
          add_ons: item.orderItemAddons.map(a => a.addon_name),
          notes: item.notes || '',
          table: tableLabel,
          status: item.item_status,
          chef: item?.primaryChef,
          station: item?.stationData,
          chef: item?.primaryChef,
          isMiscellaneous: item?.isMiscellaneous,
        });
      }
    }

    setFlattened(cards);
    setColumns(groupByStatus(cards));
  }, [rawOrders]);


  // Prepare chef options for select
  const stationOptions = useMemo(() => {
    const apiStations = stationsData?.data?.records ?? [];
    return [
      { value: 'all', label: 'All Station' },
      ...apiStations.map(c => ({ value: c.id, label: c.name })),
    ];
  }, [stationsData]);

  // Prepare global chef options (all chefs) — used as fallback
  const allChefOptions = useMemo(() => {
    const recs = chefsList?.data?.records ?? [];
    return recs.map(c => ({ id: c.id, name: c.name || c.full_name || c.display_name || `${c.first_name || ''} ${c.last_name || ''}`.trim() }));
  }, [chefsList]);


  const serveSummary = [
    {
      id: "1",
      title: "Total Items Served",
      //   subtitle: "(Today)",
      //   value: resp?.data?.stats?.totalOrdersCount,
      value: rawOrders?.data?.totalItemsServedToday || 0,
      highlight: true,
    },
    {
      id: "2",
      title: "Avg. Prep Time",
      //   subtitle: "(Today)",
      //   value: resp?.data?.stats?.totalOrdersCount,
      value: rawOrders?.data?.avgPreparationTimeToday || 0,
      highlight: true,
    },

  ];

  const handleStatusChange = async (id, from, to) => {
    if (!validTransitions[from]?.includes(to)) return;
    // find the item in flattened
    const itemObj = flattened.find(it => it.id === id);

    // If moving from ready_to_prepare and station has more than one assigned chefs -> open modal
    const stationAssigned = itemObj?.station?.chefs ?? itemObj?.station?.chefs ?? null;
    const assignedCount = Array.isArray(stationAssigned) ? stationAssigned?.length : 0;
    if (from === 'ready_to_prepare' && to === 'preparing' && assignedCount > 1) {
      // open modal and wait for user choice — actual API call happens on modal submit
      setModalOrderItem({ id, from, to, item: itemObj });
      setChefModalOpen(true);
      return;
    }
    // If only one chef present, include chef_id automatically
    const autoChefId = assignedCount === 1 ? (stationAssigned[0]?.id ?? stationAssigned[0]) : itemObj?.chef?.id ?? null;

    const previousColumns = {
      ...columns,
      [from]: [...columns[from]],
      [to]: [...columns[to]],
    };
    const prevFlattened = flattened;
    const nowIso = new Date().toISOString();

    // 3. Optimistically update the UI
    setColumns(current => ({
      ...current,
      [from]: current[from].filter(itemId => itemId !== id),
      [to]: [...current[to], id],
    }));

    setFlattened(f => f.map(item =>
      item.id === id
        ? { ...item, status: to, statusTimestamp: nowIso }
        : item
    ));
    try {
      const body = autoChefId ? { status: to, chef_id: autoChefId } : { status: to };
      const result = await changeItemStatusApi({ id: id, body }).unwrap();
      // setColumns(prev => ({
      //   ...prev,
      //   [from]: prev[from].filter(itemId => itemId !== id),
      //   [to]: [...prev[to], id]
      // }));
      if (result?.data) {
        setFlattened(f => f.map(item =>
          item.id === id
            ? { ...item, statusTimestamp: result?.data?.[`${item.item_status}_at`] }
            : item
        ));
      }
    } catch (err) {
      console.error('Failed to update status', err);
      setColumns(previousColumns);
      setFlattened(prevFlattened);
    }
  };

  // Called when modal is cancelled
  const handleModalClose = () => {
    setChefModalOpen(false);
    setModalOrderItem(null);
  };

  const handleChefChange = (value) => {
    setSelectedChef(value);
  };

  // replace onFetch=refetch with:
  const handleFetchClick = async () => {
    setIsFetchLoading(true);
    try {
      await refetch();
    } finally {
      setIsFetchLoading(false);
    }
  };

  // Modal submit: values will look like { chefId: number }
  const handleModalSubmit = async (values) => {
    // ensure we have modalOrderItem
    if (!modalOrderItem) {
      handleModalClose();
      return;
    }
    const { id, from, to, item } = modalOrderItem;
    const chefId = values.chefId ?? null;
    const chefName = (item?.station?.assignedChefs ?? []).find(c => c.id === chefId)?.name
      || allChefOptions.find(c => c.id === chefId)?.name
      || null;

    setAssignLoading(true);

    // preserve previous state for rollback
    const previousColumns = { ...columns, [from]: [...columns[from]], [to]: [...columns[to]] };
    const prevFlattened = flattened;
    const nowIso = new Date().toISOString();

    // optimistic update (assign to selected chef)
    setColumns(current => ({
      ...current,
      [from]: current[from].filter(itemId => itemId !== id),
      [to]: [...current[to], id],
    }));

    setFlattened(f =>
      f.map(it =>
        it.id === id
          ? { ...it, status: to, statusTimestamp: nowIso, chef: chefId ? { id: chefId, name: chefName, full_name: chefName } : it.chef }
          : it
      )
    );

    try {
      const body = { status: to, chef_id: chefId };
      const result = await changeItemStatusApi({ id, body }).unwrap();

      if (result?.data) {
        setFlattened(f =>
          f.map(it =>
            it.id === id
              ? { ...it, statusTimestamp: result?.data?.[`${it.item_status}_at`] || it.statusTimestamp }
              : it
          )
        );
      }
    } catch (err) {
      console.error('Failed to update status with chef', err);
      setColumns(previousColumns);
      setFlattened(prevFlattened);
    } finally {
      setAssignLoading(false);
      handleModalClose();
    }
  };

  const isOrdersLoading = isLoading || (isFetching && !rawOrders?.data);

  return (
    <>
      <ServeContainer
        orders={flattened}
        columns={columns}
        setColumns={setColumns}
        onStatusChange={handleStatusChange}
        validTransitions={validTransitions}

        stations={stationOptions}
        serveSummary={serveSummary}
        selectedChef={selectedChef}
        onChefChange={handleChefChange}
        onFetch={handleFetchClick}
        orderLoading={isFetchLoading}
        isLoading={isOrdersLoading}
      />

      <ConformChefModal
        open={chefModalOpen}
        onClose={handleModalClose}
        initialData={modalOrderItem?.item?.station ?? {}}
        onSubmit={handleModalSubmit}
        loading={assignLoading}
        // only pass assigned chefs for this station if available, otherwise pass all chefs as fallback
        chefOptions={
          (modalOrderItem?.item?.station?.chefs ?? modalOrderItem?.item?.station?.chefs) ??
          allChefOptions
        }
      />
    </>
  );
}
